import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { calcTypesApi, departmentsApi, employees, positionsApi, staffTransfers,
  EMPLOYMENT_KIND_LABELS, type EmploymentKind } from '@/api/hr';
import { orgs } from '@/api/catalogs';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useConfirm } from '@/components/ConfirmProvider';
import PlannedAccrualsTable from '@/components/PlannedAccrualsTable';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';

interface Form {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  transfer_date: string;
  new_position_id: string;
  new_department_id: string;
  // '' = не змінювати (успадкувати попередню); інакше нова форма зайнятості.
  employment_kind: '' | EmploymentKind;
  new_salary_amount: string;
  new_calc_type_id: string;
  reason: string;
  comment: string;
}

const today = new Date();
const EMPTY: Form = {
  organization_id: '', document_number: '',
  document_date: today.toISOString().slice(0, 16),
  employee_id: '',
  transfer_date: today.toISOString().slice(0, 10),
  new_position_id: '', new_department_id: '',
  employment_kind: '',
  new_salary_amount: '0',
  new_calc_type_id: '',
  reason: '', comment: '',
};

const fmt2 = (v: string | number | null) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function StaffTransferEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const [search] = useSearchParams();
  const isNew = !id || id === 'new';
  const empFromUrl = search.get('employee');

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['staff-transfer', id],
    queryFn: () => staffTransfers.get(id!),
    enabled: !isNew,
  });
  const [form, setForm] = useState<Form>(EMPTY);
  const numberPreview = useNumberPreview('doc.staff_transfers', form.organization_id, isNew);
  const v = useFormValidation();

  function validate(): boolean {
    return v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату наказу'],
      ['employee_id', form.employee_id, 'Оберіть співробітника'],
      ['transfer_date', form.transfer_date, 'Вкажіть дату переведення'],
    ]);
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  const [error, setError] = useState<string | null>(null);
  // Сторінка створення: зберегти і одразу провести (create → post → картка).
  function onSaveAndPost() {
    if (!validate()) return;
    createAndPost.mutate();
  }
  // Збережений непроведений: зберегти поточні правки і одразу провести (update → post).
  function onUpdateAndPost() {
    if (!validate()) return;
    updateAndPost.mutate();
  }

  const empListQ = useQuery({
    queryKey: ['employees-active', form.organization_id],
    queryFn: () => employees.list({
      organization_id: form.organization_id,
      active_only: true,
      limit: 1000,
    }),
    enabled: !!form.organization_id,
  });
  const posQ = useQuery({ queryKey: ['positions'], queryFn: () => positionsApi.list({ limit: 500 }) });
  const calcQ = useQuery({ queryKey: ['calc-types'], queryFn: calcTypesApi.list });
  const depQ = useQuery({
    queryKey: ['departments', form.organization_id],
    queryFn: () => departmentsApi.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });
  const empCurQ = useQuery({
    queryKey: ['employee-full', form.employee_id],
    queryFn: () => employees.get(form.employee_id),
    enabled: !!form.employee_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
        employee_id: empFromUrl ?? f.employee_id,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        employee_id: d.employee_id,
        transfer_date: d.transfer_date,
        new_position_id: d.new_position_id ?? '',
        new_department_id: d.new_department_id ?? '',
        employment_kind: d.employment_kind ?? '',
        new_salary_amount: String(d.new_salary_amount),
        new_calc_type_id: d.new_calc_type_id ?? '',
        reason: d.reason ?? '',
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data, availableOrgs, empFromUrl]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const create = useMutation({
    mutationFn: () => staffTransfers.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      employee_id: form.employee_id,
      transfer_date: form.transfer_date,
      new_position_id: form.new_position_id || null,
      new_calc_type_id: form.new_calc_type_id || null,
      new_department_id: form.new_department_id || null,
      employment_kind: form.employment_kind || null,
      new_salary_amount: form.new_salary_amount,
      reason: form.reason || null,
      comment: form.comment || null,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['staff-transfers'] });
      navigate(`/staff-transfers/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => staffTransfers.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      transfer_date: form.transfer_date,
      new_position_id: form.new_position_id || null,
      new_calc_type_id: form.new_calc_type_id || null,
      new_department_id: form.new_department_id || null,
      employment_kind: form.employment_kind || null,
      new_salary_amount: form.new_salary_amount,
      reason: form.reason || null,
      comment: form.comment || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-transfer', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: create → post одним кліком.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await staffTransfers.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        employee_id: form.employee_id,
        transfer_date: form.transfer_date,
        new_position_id: form.new_position_id || null,
        new_calc_type_id: form.new_calc_type_id || null,
        new_department_id: form.new_department_id || null,
      employment_kind: form.employment_kind || null,
        new_salary_amount: form.new_salary_amount,
        reason: form.reason || null,
        comment: form.comment || null,
      });
      await staffTransfers.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['staff-transfers'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
      navigate(`/staff-transfers/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: update → post одним кліком.
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await staffTransfers.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        transfer_date: form.transfer_date,
        new_position_id: form.new_position_id || null,
        new_calc_type_id: form.new_calc_type_id || null,
        new_department_id: form.new_department_id || null,
      employment_kind: form.employment_kind || null,
        new_salary_amount: form.new_salary_amount,
        reason: form.reason || null,
        comment: form.comment || null,
      });
      await staffTransfers.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff-transfer', id] });
      qc.invalidateQueries({ queryKey: ['staff-transfers'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => staffTransfers.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff-transfer', id] });
      qc.invalidateQueries({ queryKey: ['staff-transfers'] });
      qc.invalidateQueries({ queryKey: ['employees-full'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => staffTransfers.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff-transfers'] });
      navigate('/hr-orders');
    },
  });

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати кадрове переміщення у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/hr-orders')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий наказ (переміщення)' : `Наказ (переміщення) № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/hr-orders')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending || createAndPost.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postPending={create.isPending || update.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
          onPrint={() => navigate(`/staff-transfers/${id}/print`)}
        />
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ наказу</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата наказу</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Співробітник</label>
          <select value={form.employee_id}
                  onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">— оберіть —</option>
            {/* Поточний працівник документа — показуємо завжди (імпортовані
                переміщення посилаються на неактивних/звільнених, яких немає у
                списку active_only → ПIБ не відображалось). */}
            {empCurQ.data
              && !empListQ.data?.items.some((e) => e.id === empCurQ.data!.id) && (
              <option value={empCurQ.data.id}>
                {empCurQ.data.code ? `${empCurQ.data.code} — ` : ''}
                {empCurQ.data.full_name}
              </option>
            )}
            {empListQ.data?.items.map((e) => (
              <option key={e.id} value={e.id}>
                {e.code ? `${e.code} — ` : ''}{e.full_name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Перевести з дати</label>
          <DatePopover value={form.transfer_date}
                 onChange={(v) => setForm({ ...form, transfer_date: v })}
                 disabled={isPosted} />
        </div>
      </div>

      {empCurQ.data && (
        <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: '#f1f5f9',
                      borderRadius: 6, fontSize: '0.85rem' }}>
          <strong>Поточний стан:</strong>&nbsp;
          посада <strong>{empCurQ.data.position_name ?? '—'}</strong>,
          підрозділ <strong>{empCurQ.data.department_name ?? '—'}</strong>,
          оклад <strong>{fmt2(empCurQ.data.salary_amount)}</strong> грн.
          Незмінені поля наслідуються з поточного стану.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
        <div className="field">
          <label>Нова посада (або залишити поточну)</label>
          <select value={form.new_position_id}
                  onChange={(e) => setForm({ ...form, new_position_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— без зміни —</option>
            {posQ.data?.items.map((p) => (
              <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Новий підрозділ</label>
          <select value={form.new_department_id}
                  onChange={(e) => setForm({ ...form, new_department_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">— без зміни —</option>
            {depQ.data?.items.map((d) => (
              <option key={d.id} value={d.id}>{d.code} — {d.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Форма зайнятості (— без зміни)</label>
          <select value={form.employment_kind}
                  onChange={(e) => setForm({ ...form, employment_kind: e.target.value as '' | EmploymentKind })}
                  disabled={isPosted}
                  title="Перевести працівника на іншу форму зайнятості (напр. з основного місця на сумісництво). Порожньо — лишити поточну.">
            <option value="">— без зміни —</option>
            {(Object.entries(EMPLOYMENT_KIND_LABELS) as Array<[EmploymentKind, string]>)
              .map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Новий оклад (0 — без зміни)</label>
          <input type="number" step="0.01" value={form.new_salary_amount}
                 onChange={(e) => setForm({ ...form, new_salary_amount: e.target.value })}
                 disabled={isPosted}
                 style={{ textAlign: 'right' }} />
        </div>
        <div className="field">
          <label>Вид розрахунку (— без зміни)</label>
          <select value={form.new_calc_type_id}
                  onChange={(e) => setForm({ ...form, new_calc_type_id: e.target.value })}
                  disabled={isPosted}
                  title="Перевести працівника на інший вид розрахунку (напр. з окладу на погодинну). Порожньо — лишити поточний.">
            <option value="">— без зміни —</option>
            {/* Лише місячний оклад і погодинна (рішення власника). Решта видів
                (відрядна/за середнім) — рушій не реалізований, не пропонуємо. */}
            {(calcQ.data ?? []).filter((c) => c.code === 'SALARY' || c.code === 'HOURLY').map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        {docQuery.data?.planned_accruals?.length ? (
          <PlannedAccrualsTable lines={docQuery.data.planned_accruals} />
        ) : null}
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Причина переміщення</label>
          <input value={form.reason}
                 onChange={(e) => setForm({ ...form, reason: e.target.value })}
                 disabled={isPosted} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Коментар</label>
          <input value={form.comment}
                 onChange={(e) => setForm({ ...form, comment: e.target.value })}
                 disabled={isPosted} />
        </div>
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
